import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios, { AxiosRequestConfig } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import config from '../config';
import {
  AdminDataGrid,
  AdminFilterBar,
  AdminPageHeader,
  AdminStatusBadge,
  DataGridColumn,
  DataGridRowAction,
  FilterConfig,
  HeaderAction,
  SortDirection,
} from './components/AdminMasterDetail';
import {
  AdminBreadcrumbs,
  AdminSectionHeading,
  AdminStatCard,
} from './components/AdminUiPrimitives';
import {
  IconArchive,
  IconBook,
  IconCheckCircle,
  IconEdit,
  IconGeneral,
  IconRelationships,
} from './components/AdminIcons';

interface MyOrganization {
  membershipId: number;
  organizationId: number;
  organizationName: string;
  organizationSlug: string;
  organizationDescription: string | null;
  isDefault: boolean;
  roles: string[];
}

interface OrganizationFormData {
  name: string;
  slug: string;
  description: string;
}

type EditorMode = 'view' | 'create' | 'edit';
type SortKey = 'name' | 'slug' | 'status';

interface OrganizationRow {
  id: number;
  name: string;
  slug: string;
  status: 'Active' | 'Draft' | 'Archived';
  org: MyOrganization;
}

const DEFAULT_PAGE_SIZE = 8;

const emptyOrganizationForm: OrganizationFormData = {
  name: '',
  slug: '',
  description: '',
};

const ManageOrganizations: React.FC = () => {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();

  const apiBaseUrl = `${config.apiBaseUrl}/api/organizations`;

  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [notice, setNotice] = useState<string>('');

  const [organizations, setOrganizations] = useState<MyOrganization[]>([]);
  const [currentOrganizationId, setCurrentOrganizationId] = useState<number | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | null>(null);
  const [mode, setMode] = useState<EditorMode>('view');
  const [mobilePane, setMobilePane] = useState<'list' | 'details'>('list');

  const [organizationForm, setOrganizationForm] = useState<OrganizationFormData>(emptyOrganizationForm);

  const [searchValue, setSearchValue] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);

  const isSmallScreen = viewportWidth < 768;
  const isNarrowLayout = viewportWidth < 1140;

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const clearMessages = () => {
    setError('');
    setNotice('');
  };

  const getManagementToken = useCallback(async (): Promise<string> => {
    const tokenResponse = await getAccessTokenSilently().catch(() =>
      getAccessTokenSilently({ cacheMode: 'off' } as any)
    );

    const token =
      typeof tokenResponse === 'string'
        ? tokenResponse
        : (tokenResponse as { access_token?: string })?.access_token;

    if (!token) {
      throw new Error('Unable to get access token. Please sign in again and retry.');
    }

    return token;
  }, [getAccessTokenSilently]);

  const withAuth = useCallback(
    async (request: AxiosRequestConfig) => {
      const token = await getManagementToken();
      return axios({
        ...request,
        headers: {
          ...(request.headers ?? {}),
          Authorization: `Bearer ${token}`,
        },
      });
    },
    [getManagementToken]
  );

  const loadCurrentAndOrganizations = useCallback(async () => {
    try {
      setLoading(true);
      clearMessages();

      const currentPromise = withAuth({ method: 'get', url: `${apiBaseUrl}/current` }).catch(() => null);
      const myOrganizationsPromise = withAuth({ method: 'get', url: `${apiBaseUrl}/my-owned` });

      const [currentResponse, orgsResponse] = await Promise.all([currentPromise, myOrganizationsPromise]);

      const orgList = (orgsResponse.data ?? []) as MyOrganization[];
      setOrganizations(orgList);

      const currentOrg = currentResponse?.data as MyOrganization | undefined;
      const resolvedCurrentOrgId =
        currentOrg?.organizationId ?? orgList.find((o) => o.isDefault)?.organizationId ?? orgList[0]?.organizationId ?? null;

      setCurrentOrganizationId(resolvedCurrentOrgId);
      setSelectedOrganizationId((prev) => prev ?? resolvedCurrentOrgId);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to load organizations.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, withAuth]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void loadCurrentAndOrganizations();
  }, [isAuthenticated, loadCurrentAndOrganizations]);

  const ownerOrganizations = useMemo(() => organizations.filter((org) => org.roles.includes('Owner')), [organizations]);

  const selectedOrganization = useMemo(
    () => ownerOrganizations.find((o) => o.organizationId === selectedOrganizationId) || null,
    [ownerOrganizations, selectedOrganizationId]
  );

  const rows = useMemo<OrganizationRow[]>(
    () =>
      ownerOrganizations.map((org) => ({
        id: org.organizationId,
        name: org.organizationName,
        slug: org.organizationSlug,
        status: org.isDefault ? 'Active' : 'Draft',
        org,
      })),
    [ownerOrganizations]
  );

  const filteredRows = useMemo(() => {
    const search = searchValue.trim().toLowerCase();

    return rows.filter((row) => {
      if (search) {
        const haystack = [row.name, row.slug, row.org.organizationDescription || '', row.org.roles.join(' ')].join(' ').toLowerCase();
        if (!haystack.includes(search)) {
          return false;
        }
      }

      if (statusFilter === 'default' && !row.org.isDefault) {
        return false;
      }

      if (statusFilter === 'nondefault' && row.org.isDefault) {
        return false;
      }

      return true;
    });
  }, [rows, searchValue, statusFilter]);

  const sortedRows = useMemo(() => {
    const sorted = [...filteredRows];
    const direction = sortDirection === 'asc' ? 1 : -1;

    sorted.sort((first, second) => {
      const left = String(first[sortKey]).toLowerCase();
      const right = String(second[sortKey]).toLowerCase();
      if (left < right) {
        return -1 * direction;
      }
      if (left > right) {
        return 1 * direction;
      }
      return 0;
    });

    return sorted;
  }, [filteredRows, sortDirection, sortKey]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / DEFAULT_PAGE_SIZE));

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * DEFAULT_PAGE_SIZE;
    return sortedRows.slice(start, start + DEFAULT_PAGE_SIZE);
  }, [page, sortedRows]);

  const syncFormWithOrganization = (org: MyOrganization, nextMode: EditorMode) => {
    setMode(nextMode);
    setSelectedOrganizationId(org.organizationId);
    setOrganizationForm({
      name: org.organizationName,
      slug: org.organizationSlug,
      description: org.organizationDescription ?? '',
    });
  };

  const handleSelectOrganization = (org: MyOrganization) => {
    syncFormWithOrganization(org, 'view');
    clearMessages();
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const handleStartEdit = (org: MyOrganization) => {
    syncFormWithOrganization(org, 'edit');
    clearMessages();
    if (isSmallScreen) {
      setMobilePane('details');
    }
  };

  const handleSave = async () => {
    if (!organizationForm.name.trim() || !organizationForm.slug.trim()) {
      setError('Organization name and slug are required.');
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      if (mode === 'edit' && selectedOrganizationId) {
        await withAuth({
          method: 'patch',
          url: `${apiBaseUrl}/${selectedOrganizationId}`,
          data: {
            name: organizationForm.name.trim(),
            slug: organizationForm.slug.trim().toLowerCase(),
            description: organizationForm.description.trim() || null,
          },
        });
        setNotice('Organization updated successfully.');
      }

      setMode('view');
      await loadCurrentAndOrganizations();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to save organization.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (selectedOrganization) {
      syncFormWithOrganization(selectedOrganization, 'view');
    } else {
      setMode('view');
      setOrganizationForm(emptyOrganizationForm);
    }
  };

  const handleSetDefault = async (organizationId: number) => {
    try {
      setSaving(true);
      clearMessages();

      await withAuth({ method: 'post', url: `${apiBaseUrl}/${organizationId}/set-default` });
      setNotice('Default organization updated.');
      setCurrentOrganizationId(organizationId);
      await loadCurrentAndOrganizations();
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to set default organization.';
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToMemberships = (organizationId: number) => {
    const params = new URLSearchParams({ organizationId: String(organizationId) });
    navigate(`/manage-organization-memberships?${params.toString()}`);
  };

  const handleSort = (columnKey: string) => {
    if (!['name', 'slug', 'status'].includes(columnKey)) {
      return;
    }

    const nextKey = columnKey as SortKey;
    if (sortKey === nextKey) {
      setSortDirection((current) => (current === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setSortKey(nextKey);
    setSortDirection('asc');
  };

  const columns = useMemo<DataGridColumn<OrganizationRow>[]>(() => {
    const baseColumns: DataGridColumn<OrganizationRow>[] = [
      {
        key: 'name',
        label: 'Name',
        sortable: true,
        width: isSmallScreen ? '62%' : '34%',
        render: (row) => (
          <div style={{ minWidth: isSmallScreen ? 0 : 180 }}>
            <div style={{ fontWeight: 700, color: '#142541', overflowWrap: 'anywhere' }}>{row.name}</div>
            <div style={{ fontSize: '0.75rem', color: '#607399', marginTop: 2 }}>{row.slug}</div>
          </div>
        ),
      },
    ];

    if (!isSmallScreen) {
      baseColumns.push({
        key: 'slug',
        label: 'Slug',
        sortable: true,
        width: '28%',
        render: (row) => row.slug,
      });
    }

    baseColumns.push({
      key: 'status',
      label: 'Status',
      sortable: true,
      width: isSmallScreen ? '20%' : '14%',
      render: (row) => <AdminStatusBadge status={row.status} />,
    });

    return baseColumns;
  }, [isSmallScreen]);

  const filterConfigs = useMemo<FilterConfig[]>(
    () => [
      {
        key: 'status',
        label: 'View',
        value: statusFilter,
        onChange: (value) => {
          setStatusFilter(value);
          setPage(1);
        },
        options: [
          { value: 'all', label: 'All Organizations' },
          { value: 'default', label: 'Default Only' },
          { value: 'nondefault', label: 'Non-default' },
        ],
      },
    ],
    [statusFilter]
  );

  const rowActions: DataGridRowAction<OrganizationRow>[] = [
    {
      key: 'default',
      label: 'Set as default',
      icon: <IconCheckCircle size={14} />,
      onClick: (row) => {
        void handleSetDefault(row.org.organizationId);
      },
      isDisabled: (row) => saving || currentOrganizationId === row.org.organizationId,
    },
    {
      key: 'memberships',
      label: 'Open memberships',
      icon: <IconRelationships size={14} />,
      onClick: (row) => handleGoToMemberships(row.org.organizationId),
      isDisabled: () => saving,
    },
    {
      key: 'edit',
      label: 'Edit organization',
      icon: <IconEdit size={14} />,
      onClick: (row) => handleStartEdit(row.org),
      isDisabled: () => saving,
    },
  ];

  const actions: HeaderAction[] = [
    { key: 'refresh', label: 'Refresh', variant: 'secondary', onClick: () => { void loadCurrentAndOrganizations(); } },
  ];

  const totalCount = rows.length;
  const defaultCount = rows.filter((row) => row.org.isDefault).length;
  const ownerRoleCount = rows.filter((row) => row.org.roles.includes('Owner')).length;

  const showListPanel = !isSmallScreen || mobilePane === 'list';
  const showDetailsPanel = !isSmallScreen || mobilePane === 'details';
  const hasEditableTarget = Boolean(selectedOrganization);

  if (!isAuthenticated) {
    return (
      <div style={{ maxWidth: 720, margin: '32px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Organizations</h2>
        <p style={{ marginBottom: 16 }}>Please sign in to manage organizations.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '10px 16px',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          Sign In
        </button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', minHeight: 'calc(100vh - 260px)', display: 'grid', gap: 12 }}>
      <AdminBreadcrumbs
        items={[
          { label: 'Administration', to: '/analytics' },
          {
            label: 'Organizations',
            onClick: () => {
              if (isSmallScreen) {
                setMobilePane('list');
              }
            },
          },
          {
            label: selectedOrganization?.organizationName || 'Organizations',
            isCurrent: true,
          },
        ]}
      />

      <AdminPageHeader
        title="Organizations"
        subtitle={`${ownerOrganizations.length} owner organizations • ${selectedIds.length} selected`}
        actions={actions}
      />

      {(error || notice) && (
        <div
          style={{
            borderRadius: 10,
            border: `1px solid ${error ? '#fecaca' : '#bfdbfe'}`,
            background: error ? '#fff1f2' : '#eff6ff',
            color: error ? '#b91c1c' : '#1d4ed8',
            padding: '10px 12px',
            fontSize: '0.84rem',
          }}
        >
          {error || notice}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 10,
        }}
      >
        <AdminStatCard label="Total" value={totalCount} icon={<IconBook size={15} />} />
        <AdminStatCard label="Default" value={defaultCount} icon={<IconCheckCircle size={15} />} accent="active" />
        <AdminStatCard label="Owner Role" value={ownerRoleCount} icon={<IconRelationships size={15} />} />
        <AdminStatCard label="Non-default" value={Math.max(totalCount - defaultCount, 0)} icon={<IconArchive size={15} />} accent="archived" />
      </div>

      <AdminFilterBar
        searchValue={searchValue}
        onSearchChange={(value) => {
          setSearchValue(value);
          setPage(1);
        }}
        searchPlaceholder="Search organizations by name, slug, roles..."
        filters={filterConfigs}
      />

      {isSmallScreen && (
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid #d8e3f6',
            borderRadius: 11,
            background: '#f6f9ff',
            padding: 4,
            width: 'fit-content',
          }}
        >
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'list' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('list')}
          >
            List
          </button>
          <button
            type="button"
            className={`amd-btn ${mobilePane === 'details' ? 'amd-btn-primary' : 'amd-btn-ghost'}`}
            onClick={() => setMobilePane('details')}
          >
            Details
          </button>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isNarrowLayout ? '1fr' : 'minmax(420px, 58%) minmax(320px, 42%)',
          gap: 12,
          minHeight: isNarrowLayout ? 'auto' : 560,
        }}
      >
        {showListPanel && (
          <div style={{ minHeight: 0 }}>
            {loading ? (
              <div style={{ border: '1px solid #dfe8f8', borderRadius: 12, padding: 22, background: '#fff' }}>
                Loading organizations...
              </div>
            ) : (
              <AdminDataGrid<OrganizationRow>
                rows={pagedRows}
                columns={columns}
                selectedRowId={selectedOrganizationId}
                onRowClick={(row) => handleStartEdit(row.org)}
                selectedIds={selectedIds}
                onSelectedIdsChange={setSelectedIds}
                showSelection={!isSmallScreen}
                sortKey={sortKey}
                sortDirection={sortDirection}
                onSort={handleSort}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                totalCount={sortedRows.length}
                onPageChange={setPage}
                emptyMessage="No owner organizations found."
                rowActions={rowActions}
              />
            )}
          </div>
        )}

        {showDetailsPanel && (
          <div
            style={{
              border: '1px solid #dfe8f8',
              borderRadius: 12,
              background: '#fff',
              boxShadow: '0 8px 18px rgba(18, 42, 80, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <div style={{ padding: '14px 14px 10px', borderBottom: '1px solid #edf2fb' }}>
              {isSmallScreen && (
                <button
                  type="button"
                  className="amd-btn amd-btn-ghost"
                  style={{ marginBottom: 8 }}
                  onClick={() => setMobilePane('list')}
                >
                  Back to List
                </button>
              )}
              <h3
                style={{
                  margin: 0,
                  width: '100%',
                  boxSizing: 'border-box',
                  fontSize: '1.02rem',
                  fontWeight: 800,
                  color: '#173159',
                  background: hasEditableTarget
                    ? 'linear-gradient(180deg, #f5f9ff 0%, #ebf3ff 100%)'
                    : 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                  border: hasEditableTarget ? '1px solid #cfe0fb' : '1px solid #d9e2ef',
                  borderRadius: 10,
                  padding: '9px 11px',
                }}
              >
                {mode === 'edit' ? 'Editing Organization' : selectedOrganization ? selectedOrganization.organizationName : 'Select an organization'}
              </h3>
            </div>

            <div style={{ padding: 14, overflowY: 'auto', minHeight: 0 }}>
              {!hasEditableTarget ? (
                <div
                  style={{
                    minHeight: 260,
                    border: '1px dashed #c9d6ef',
                    borderRadius: 12,
                    background: '#f8fbff',
                    display: 'grid',
                    placeContent: 'center',
                    textAlign: 'center',
                    color: '#4f658f',
                    gap: 8,
                    padding: 16,
                  }}
                >
                  <div style={{ color: '#35568a', display: 'inline-flex', justifySelf: 'center' }}><IconBook size={26} /></div>
                  <div style={{ fontWeight: 700 }}>Select an organization from the list</div>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 18 }}>
                  <section style={{ display: 'grid', gap: 12, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                    <AdminSectionHeading icon={<IconGeneral size={14} />} title="General" />
                    <label style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Organization Name</span>
                      <input
                        type="text"
                        value={organizationForm.name}
                        onChange={(e) => setOrganizationForm((prev) => ({ ...prev, name: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </label>
                    <label style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Slug</span>
                      <input
                        type="text"
                        value={organizationForm.slug}
                        onChange={(e) => setOrganizationForm((prev) => ({ ...prev, slug: e.target.value }))}
                        disabled={mode === 'view'}
                      />
                    </label>
                    <label style={{ textAlign: 'left' }}>
                      <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: '#445b84', marginBottom: 6 }}>Description</span>
                      <textarea
                        rows={5}
                        value={organizationForm.description}
                        onChange={(e) => setOrganizationForm((prev) => ({ ...prev, description: e.target.value }))}
                        disabled={mode === 'view'}
                        style={{ width: '100%', boxSizing: 'border-box' }}
                      />
                    </label>
                  </section>

                  {selectedOrganization && (
                    <>
                      <section style={{ display: 'grid', gap: 10, paddingBottom: 14, borderBottom: '1px solid #edf2fb' }}>
                        <AdminSectionHeading icon={<IconRelationships size={14} />} title="Actions" />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          <button
                            type="button"
                            className="amd-btn amd-btn-secondary"
                            onClick={() => {
                              void handleSetDefault(selectedOrganization.organizationId);
                            }}
                            disabled={saving || currentOrganizationId === selectedOrganization.organizationId}
                          >
                            Set Default
                          </button>
                          <button
                            type="button"
                            className="amd-btn amd-btn-ghost"
                            onClick={() => handleGoToMemberships(selectedOrganization.organizationId)}
                            disabled={saving}
                          >
                            Memberships
                          </button>
                        </div>
                      </section>

                    </>
                  )}
                </div>
              )}
            </div>

            {mode === 'edit' && (
              <div
                style={{
                  borderTop: '1px solid #e4ecf9',
                  background: 'rgba(255,255,255,0.98)',
                  padding: '10px 12px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 8,
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 4,
                }}
              >
                <button type="button" className="amd-btn amd-btn-secondary" onClick={handleCancel} disabled={saving}>
                  Cancel
                </button>
                <button type="button" className="amd-btn amd-btn-primary" onClick={() => { void handleSave(); }} disabled={saving}>
                  Save Changes
                </button>
              </div>
            )}

            {mode === 'view' && selectedOrganization && (
              <div style={{ borderTop: '1px solid #e4ecf9', padding: '10px 12px', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                <button type="button" className="amd-btn amd-btn-secondary" onClick={() => handleStartEdit(selectedOrganization)} disabled={saving} style={{ marginLeft: 'auto' }}>
                  Edit Organization
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ManageOrganizations;
